import { useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Screen } from '@/components/ui';
import BidStep from '@/components/BidStep';
import TricksStep from '@/components/TricksStep';
import Scorecard from '@/components/Scorecard';
import StatsPanel from '@/components/StatsPanel';
import FinalStandings from '@/components/FinalStandings';
import { currentRoundOf, selectGame, useScorekeeper } from '@/store/scorekeeper';
import { plural } from '@/lib/labels';

type Tab = 'ronde' | 'stand' | 'statistiek';

export default function PlayGame() {
  const { gameId } = useParams<{ gameId: string }>();
  const game = useScorekeeper(selectGame(gameId));
  const [tab, setTab] = useState<Tab>('ronde');

  // A deleted or unknown game should not leave a blank screen behind.
  if (!game || !gameId) return <Navigate to="/" replace />;

  const round = currentRoundOf(game);
  const dealer = round ? game.seats[round.dealerSeat] : null;

  return (
    <Screen
      title={round ? `Ronde ${round.roundNo} van ${game.rounds.length}` : 'Eindstand'}
      back="/"
      subtitle={
        round
          ? `${round.cards} ${plural(round.cards, 'kaart', 'kaarten')} · ${dealer?.displayName} deelt`
          : `${game.rounds.length} rondes gespeeld`
      }
    >
      <nav className="flex gap-1 rounded-xl bg-base-800/70 p-1">
        {(['ronde', 'stand', 'statistiek'] as Tab[]).map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setTab(option)}
            aria-current={tab === option}
            className={[
              'min-h-10 flex-1 rounded-lg text-sm font-medium capitalize transition-colors',
              tab === option ? 'bg-base-600 text-white' : 'text-slate-400 hover:text-white',
            ].join(' ')}
          >
            {option}
          </button>
        ))}
      </nav>

      {tab === 'ronde' &&
        (round === undefined ? (
          <FinalStandings game={game} />
        ) : round.status === 'bidding' ? (
          <BidStep game={game} round={round} />
        ) : (
          <TricksStep game={game} round={round} />
        ))}

      {tab === 'stand' && <Scorecard game={game} />}

      {tab === 'statistiek' && <StatsPanel game={game} />}
    </Screen>
  );
}
